/**
 * Site sohbetleri (v0.1.248): every chat a visitor had with the cadgaragepro.com assistant, newest first, read only.
 * Turkish first (the visitor's question and the assistant's answer as the assistant translated them), the visitor's
 * own words and the answer they got under it when the language differs, and whether the assistant offered "Send to
 * our team". Rows are deleted after 90 days, like the chats themselves.
 */

import type { SiteChatThread, SiteChatTurnView, SiteFormRequestView } from '@twin/contracts';
import { useEffect } from 'react';
import { useSiteChats } from '../app/site-chats.js';

const WHEN = new Intl.DateTimeFormat('tr-TR', {
  timeZone: 'Europe/Istanbul',
  day: 'numeric',
  month: 'long',
  hour: '2-digit',
  minute: '2-digit',
});

const same = (a: string | null, b: string | null) =>
  a !== null && b !== null && a.trim().toLocaleLowerCase('tr') === b.trim().toLocaleLowerCase('tr');

function Turn({ turn }: { turn: SiteChatTurnView }) {
  return (
    <li className="sc-turn">
      <p className="sc-turn__when">{WHEN.format(new Date(turn.at))}</p>
      <p>
        <strong>Ziyaretçi:</strong> {turn.questionTr ?? turn.question}
      </p>
      {turn.questionTr !== null && !same(turn.questionTr, turn.question) ? (
        <p className="sc-turn__orig">Kendi yazdığı: {turn.question}</p>
      ) : null}
      {turn.answered && turn.answer !== null ? (
        <>
          <p>
            <strong>Asistan:</strong> {turn.answerTr ?? turn.answer}
          </p>
          {turn.answerTr !== null && !same(turn.answerTr, turn.answer) ? (
            <p className="sc-turn__orig">Ziyaretçiye giden: {turn.answer}</p>
          ) : null}
        </>
      ) : (
        <p>{turn.waiting ? 'Asistanın cevabı bekleniyor.' : 'Asistan cevap veremedi.'}</p>
      )}
      {turn.handoff ? <p className="sc-turn__tag">Asistan “Ekibe gönder” seçeneğini önerdi.</p> : null}
    </li>
  );
}

const FORM_KINDS: Record<SiteFormRequestView['kind'], string> = {
  free_cad: 'Ücretsiz küçük CAD işi',
  question: 'Proje sorusu',
  notify: 'Satışa çıkınca haber ver',
  other: 'Site talebi',
};

/** v0.1.256: a request sent with the site form or "Send to our team"; its reply goes through Onaylar. */
function FormRequest({ form }: { form: SiteFormRequestView }) {
  return (
    <article className="sc-chat" aria-label={`Form talebi, ${WHEN.format(new Date(form.at))}`}>
      <header className="sc-chat__head">
        <span className="sc-chat__page">{FORM_KINDS[form.kind]}</span>
        <span className="sc-chat__count">
          {WHEN.format(new Date(form.at))} · {form.answered ? 'cevap gitti' : 'cevap bekliyor (Onaylar)'}
        </span>
      </header>
      <ol className="sc-chat__turns">
        <li className="sc-turn">
          {form.subject === '' ? null : <p className="sc-turn__when">{form.subject}</p>}
          <p>
            <strong>Ziyaretçi:</strong> {form.textTr ?? 'Mesaj metni yok.'}
          </p>
        </li>
      </ol>
    </article>
  );
}

function Chat({ chat }: { chat: SiteChatThread }) {
  const started = WHEN.format(new Date(chat.startedAt));
  return (
    <article className="sc-chat" aria-label={`Sohbet, ${started}`}>
      <header className="sc-chat__head">
        <span className="sc-chat__page">{chat.page}</span>
        <span className="sc-chat__count">
          {started} · {chat.turns.length} mesaj
        </span>
      </header>
      <ol className="sc-chat__turns">
        {chat.turns.map((turn) => (
          <Turn key={turn.turnId} turn={turn} />
        ))}
      </ol>
    </article>
  );
}

export function SiteChatsPage() {
  const { store, state } = useSiteChats();
  useEffect(() => {
    void store.reload();
  }, [store]);
  if (state.chats === null) {
    return state.error === null ? (
      <div className="loading" role="status">
        Yükleniyor…
      </div>
    ) : (
      <div className="shop-status" role="status">
        Site sohbetleri yüklenemedi: {state.error}{' '}
        <button type="button" className="btn btn--secondary" onClick={() => void store.reload()}>
          Tekrar dene
        </button>
      </div>
    );
  }
  return (
    <div className="sc">
      <p className="sc__lede">
        Ziyaretçilerin site asistanıyla yazışmaları, en yenisi üstte. Her sohbetin ilk mesajı Telegram’a da gelir.
        Sohbetler 90 gün sonra silinir.
      </p>
      {state.forms.length === 0 ? null : (
        <section aria-label="Site formundan gelenler">
          <h3 className="sc__lede">
            <strong>Site formundan gelenler</strong> (ücretsiz CAD işi, proje sorusu, “Ekibe gönder”); cevapları
            Onaylar’dan gider.
          </h3>
          <ul className="sc__list" aria-label="Site formu talepleri">
            {state.forms.map((form) => (
              <li key={form.conversationId}>
                <FormRequest form={form} />
              </li>
            ))}
          </ul>
        </section>
      )}
      {state.chats.length === 0 ? (
        <p className="sc__lede">Henüz site sohbeti yok.</p>
      ) : (
        <ul className="sc__list" aria-label="Site sohbetleri">
          {state.chats.map((chat) => (
            <li key={chat.chatId}>
              <Chat chat={chat} />
            </li>
          ))}
        </ul>
      )}
      <div className="sc__actions">
        <button
          type="button"
          className="btn btn--secondary"
          onClick={() => void store.reload()}
          disabled={state.loading}
        >
          Yenile
        </button>
        {state.more ? (
          <button
            type="button"
            className="btn btn--secondary"
            onClick={() => void store.older()}
            disabled={state.loading}
          >
            Daha eski sohbetler
          </button>
        ) : null}
      </div>
    </div>
  );
}
